class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();

    this.initRecommendationButtons();
    this.fetchFallbackRecommendationTitles();
    this.updateRecommendationState();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    this.fetchFallbackRecommendationTitles();
    this.updateRecommendationState();

    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true },
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
      this.fetchFallbackRecommendationTitles();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
        section: '#cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }

  initRecommendationButtons() {
    if (this._recsButtonsBound) return;
    this._recsButtonsBound = true;
    this.addEventListener('click', (event) => {
      const btn = event.target.closest('.recommendation-add-to-cart');
      if (!btn) return;
      event.preventDefault();
      this.handleRecommendationAddToCart(btn);
    });
  }

  async handleRecommendationAddToCart(button) {
    const variantId = button.dataset.variantId;
    const productTitle = button.dataset.productTitle;
    const quantity = parseInt(button.dataset.quantity || '1', 10) || 1;

    const addingText = button.dataset.addingText || 'Adding...';
    const addedText = button.dataset.addedText || 'Added!';

    if (!variantId) {
      console.error('No variant ID found');
      return;
    }

    const originalText = button.innerHTML;
    button.disabled = true;
    button.innerHTML = addingText;

    const stockMessage =
      button.dataset.stockMessage || window.variantStrings?.inStockDelivery || 'In Stock, delivered within 7 days';

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          id: variantId,
          quantity: quantity,
          properties: { Delivery: stockMessage },
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();

      button.innerHTML = addedText;

      await this.refreshCartDrawer();
    } catch (error) {
      console.error('Error adding to cart:', error);

      // Show error state
      button.innerHTML = 'Error';

      setTimeout(() => {
        button.innerHTML = originalText;
        button.disabled = false;
      }, 2000);
    }
  }

  updateRecommendationState() {
    const metafieldWrapper = this.querySelector('[data-metafield-recommendations]');
    if (metafieldWrapper && metafieldWrapper.querySelector('.recommendation-card')) {
      this.classList.remove('no-recommendations');
      return;
    }

    const fallback = this.querySelector('[data-fallback-recommendations]');
    if (fallback) {
      const hasFallbackCards = !!fallback.querySelector('.recommendation-card');
      if (hasFallbackCards) {
        this.classList.remove('no-recommendations');
      } else if (fallback.dataset.loaded === 'true') {
        this.classList.add('no-recommendations');
      }
    }
  }

  async refreshCartDrawer() {
    try {
      const response = await fetch(`${window.location.pathname}?section_id=cart-drawer`);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const html = await response.text();
      const parser = new DOMParser();
      const newDocument = parser.parseFromString(html, 'text/html');

      const currentDrawer = this.querySelector('.drawer__inner');
      const newDrawer = newDocument.querySelector('.drawer__inner');

      if (currentDrawer && newDrawer) {
        currentDrawer.innerHTML = newDrawer.innerHTML;
      }

      const cartIconBubble = document.querySelector('#cart-icon-bubble');
      const newCartIconBubble = newDocument.querySelector('#cart-icon-bubble');

      if (cartIconBubble && newCartIconBubble) {
        cartIconBubble.innerHTML = newCartIconBubble.innerHTML;
      }

      this.initRecommendationButtons();
      this.updateRecommendationState();
      const fallback = this.querySelector('[data-fallback-recommendations]');
      if (fallback && fallback.dataset.loaded !== 'true' && fallback.dataset.loaded !== 'pending') {
        this.fetchFallbackRecommendationTitles();
      }
    } catch (error) {
      console.error('Error refreshing cart drawer:', error);
    }
  }

  fetchFallbackRecommendationTitles() {
    const container = this.querySelector('[data-fallback-recommendations]');
    if (!container) return;

    const cartProductIds = Array.from(this.querySelectorAll('.cart-item[data-product-id]'))
      .map((el) => el.dataset.productId)
      .filter((v, i, arr) => arr.indexOf(v) === i);

    if (!cartProductIds.length) return;

    const grid = container.querySelector('[data-fallback-grid]');
    if (!grid) return;

    const limit = parseInt(container.dataset.limit || '5', 10);

    const sourceSignature = cartProductIds.join(',');
    const lastSignature = container.dataset.lastSources;

    if (container.dataset.loaded === 'true' && lastSignature === sourceSignature) {
      return;
    }
    if (container.dataset.loaded === 'pending' && lastSignature === sourceSignature) {
      return;
    }

    container.dataset.loaded = 'pending';
    container.dataset.lastSources = sourceSignature;

    const cartIdsSet = new Set(cartProductIds.map((id) => parseInt(id, 10)));
    const collected = [];
    const seen = new Set(cartIdsSet);

    const parser = new DOMParser();

    const fetchNext = async (index = 0) => {
      if (collected.length >= limit) return;
      if (index >= cartProductIds.length) return;

      const baseId = cartProductIds[index];
      const remaining = limit - collected.length;

      try {
        const url =
          `${window.Shopify.routes.root}recommendations/products` +
          `?product_id=${baseId}` +
          `&limit=${remaining}` +
          `&section_id=cart-recommendations-fallback`;

        const res = await fetch(url);
        if (!res.ok) throw new Error(res.status);

        const html = await res.text();
        const doc = parser.parseFromString(html, 'text/html');
        const cards = Array.from(doc.querySelectorAll('.recommendation-card[data-product-id]'));

        for (const card of cards) {
          if (collected.length >= limit) break;

          const productId = parseInt(card.dataset.productId, 10);
          if (!productId || seen.has(productId)) continue;

          seen.add(productId);
          collected.push(card.outerHTML);
        }
      } catch (e) {
        console.warn('Fetch recommendations error', e);
      }

      if (collected.length < limit) {
        return fetchNext(index + 1);
      }
    };

    fetchNext().then(() => {
      if (!collected.length) {
        container.dataset.loaded = 'true';
        grid.innerHTML = '';
        this.classList.add('no-recommendations');
        return;
      }

      grid.innerHTML = collected.slice(0, limit).join('');
      container.dataset.loaded = 'true';
      this.classList.remove('no-recommendations');
      this.initRecommendationButtons();
      this.updateRecommendationState();
    });    
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
